import { FeatureFlag, get as getFeatureFlag } from '../FeatureFlagService';
import KnowledgeBaseService from '../KnowledgeBaseService';
import { logError } from '../LoggingService/LoggingService';
import LegacyZendeskService, {
  defaultZendeskSettings,
  ZENDESK_DESCRIPTION_FIELD_ID,
  ZENDESK_SUBJECT_FIELD_ID,
  ZENDESK_TICKET_FORM_ID,
} from '../ZendeskService/LegacyZendeskService';
import MessagingZendeskService from '../ZendeskService/MessagingZendeskService';
import { ZendeskApiKeyError, type ZendeskSettings } from '../ZendeskService/types';
import { hasZendeskApiInitialized } from '../ZendeskService/utils';

import { type HelpWidgetService } from './types';

function openKnowledgeBaseInNewTab() {
  window.open('https://support.focus.teamleader.eu', '_blank');
}

function initializeLegacy(): void {
  LegacyZendeskService.initialize()
    .then((zendesk) => zendesk('webWidget', 'helpCenter:setSuggestions', { url: true }))
    .catch((error) => {
      if (error instanceof ZendeskApiKeyError) {
        // API key not set
        return;
      }

      logError(error);
    });
}

function initializeMessaging(): void {
  MessagingZendeskService.initialize().catch((error) => {
    if (error instanceof ZendeskApiKeyError) {
      // API key not set
      return;
    }

    logError(error);
  });
}

function initialize(): void {
  const hasZendeskMessagingEnabled = getFeatureFlag(FeatureFlag.EnableZendeskMessaging);

  if (hasZendeskMessagingEnabled) {
    initializeMessaging();
  } else {
    initializeLegacy();
  }
}

function showLegacy(): void {
  if (hasZendeskApiInitialized(window)) {
    LegacyZendeskService.zendesk('webWidget', 'helpCenter:setSuggestions', { url: true });

    LegacyZendeskService.zendesk(
      'webWidget',
      'updateSettings',
      Object.assign<{}, ZendeskSettings, Partial<ZendeskSettings>>({}, window.zESettings ?? defaultZendeskSettings, {
        webWidget: {
          chat: {
            suppress: true,
          },
          contactForm: {
            suppress: false,
            ticketForms: [{ id: ZENDESK_TICKET_FORM_ID }],
          },
          helpCenter: {
            suppress: false,
          },
        },
      }),
    );

    LegacyZendeskService.zendesk('webWidget', 'show');
    LegacyZendeskService.zendesk('webWidget', 'open');
  } else {
    openKnowledgeBaseInNewTab();
  }
}

function showMessaging(): void {
  KnowledgeBaseService.open();
}

function show(): void {
  const hasZendeskMessagingEnabled = getFeatureFlag(FeatureFlag.EnableZendeskMessaging);

  if (hasZendeskMessagingEnabled) {
    showMessaging();
  } else {
    showLegacy();
  }
}

function showLegacyContactUs(subject: string, description: string): void {
  if (hasZendeskApiInitialized(window)) {
    LegacyZendeskService.zendesk(
      'webWidget',
      'updateSettings',
      Object.assign<{}, ZendeskSettings, Partial<ZendeskSettings>>({}, window.zESettings ?? defaultZendeskSettings, {
        webWidget: {
          chat: {
            suppress: true,
          },
          contactForm: {
            suppress: false,
            ticketForms: [{ id: ZENDESK_TICKET_FORM_ID }],
            fields: [
              { id: ZENDESK_SUBJECT_FIELD_ID, prefill: { '*': subject } },
              { id: ZENDESK_DESCRIPTION_FIELD_ID, prefill: { '*': description } },
            ],
          },
          helpCenter: { suppress: true },
        },
      }),
    );

    LegacyZendeskService.zendesk('webWidget', 'show');
    LegacyZendeskService.zendesk('webWidget', 'open');
  } else {
    openKnowledgeBaseInNewTab();
  }
}

function showMessagingContactUs() {
  KnowledgeBaseService.openContactForm();
}

function showContactUs(subject: string, description: string): void {
  const hasZendeskMessagingEnabled = getFeatureFlag(FeatureFlag.EnableZendeskMessaging);

  if (hasZendeskMessagingEnabled) {
    showMessagingContactUs();
  } else {
    showLegacyContactUs(subject, description);
  }
}

const ZendeskHelpWidgetService: HelpWidgetService = {
  initialize,
  show,
  showContactUs,
};

export default ZendeskHelpWidgetService;
